/**
 * UI-8c Task 5 — useKeyRotation hook.
 *
 * Drives the four platform key-rotation endpoints:
 *
 *   GET  /platform/key-rotation/status            → list of {KeyRotationStatus}
 *   POST /platform/key-rotation/:slug/trigger     → 202 enqueue immediate rotation
 *   POST /platform/key-rotation/:slug/schedule    → 201 with { scheduledFor: ISO }
 *   GET  /platform/key-rotation/:slug/history     → recent sox_control_log entries
 *
 * The hook is NOT built on `useAdminResource` because the status endpoint
 * returns a custom envelope (`{ tenants: KeyRotationStatus[] }` where each
 * entry is a status snapshot, not a "resource" the page CRUDs). Instead we
 * drive the status load with a plain `useEffect + api.get`, and expose the
 * mutation helpers as direct `api.post` / `api.get` calls.
 *
 * After `trigger()` or `schedule()` succeeds we refetch the status so the
 * table flips to the new state ("Scheduled" badge, fresh lastRotationAt, …).
 *
 * All routes are gated server-side by `PLATFORM_KEY_ROTATION`; the page
 * (KeyRotationPage) wraps itself in <RequirePermission> for the client gate.
 *
 * Note on api.post(202): the project's `api.post` does not take an
 * `expect: 202` option — any 2xx status is treated as success and the parsed
 * JSON body (`{ jobEnqueued: true }`) is returned as-is.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface KeyRotationStatus {
  slug: string
  name: string
  lastRotationAt: string | null
  kekVersion: number | null
  dekCount: number | null
  scheduledFor: string | null
}

export interface SoxEntry {
  id: string
  controlRef: string
  actorId: string
  status: string
  evidence: unknown
  recordedAt: string
}

export interface UseKeyRotationResult {
  tenants: KeyRotationStatus[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  /** POST /platform/key-rotation/:slug/trigger — enqueues an immediate rotation. */
  trigger: (slug: string) => Promise<void>
  /** POST /platform/key-rotation/:slug/schedule — schedules a future rotation. */
  schedule: (slug: string, scheduledFor: Date) => Promise<void>
  /** GET /platform/key-rotation/:slug/history — recent SOX log entries. */
  history: (slug: string) => Promise<{ entries: SoxEntry[] }>
}

const BASE_PATH = '/platform/key-rotation'

export function useKeyRotation(): UseKeyRotationResult {
  const [tenants, setTenants] = useState<KeyRotationStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ tenants: KeyRotationStatus[] }>(
        `${BASE_PATH}/status`,
      )
      setTenants(res.tenants ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load key rotation status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const trigger = useCallback(
    async (slug: string): Promise<void> => {
      await api.post(`${BASE_PATH}/${slug}/trigger`)
      await refetch()
    },
    [refetch],
  )

  const schedule = useCallback(
    async (slug: string, scheduledFor: Date): Promise<void> => {
      await api.post(`${BASE_PATH}/${slug}/schedule`, {
        scheduledFor: scheduledFor.toISOString(),
      })
      await refetch()
    },
    [refetch],
  )

  const history = useCallback(
    async (slug: string): Promise<{ entries: SoxEntry[] }> => {
      return api.get<{ entries: SoxEntry[] }>(`${BASE_PATH}/${slug}/history`)
    },
    [],
  )

  return {
    tenants,
    loading,
    error,
    refetch,
    trigger,
    schedule,
    history,
  }
}
